import type { IRouteDefns, IRouteName, IRouteProps } from 'router';
import { generatePath, matchPath, useLocation } from 'react-router-dom';
import { withSkin, SKIN_ROUTE_PATTERN } from './solutionUrl';

const subPath = ':subPath(.*)*';
const UUID =
  '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';
const nameId = (name: string) => `:${name}(${UUID})`;

const tenderId = nameId('tenderId');
const orderId = nameId('orderId');

const routes: IRouteDefns = {
  /****** Tenders ******/
  tenders: {
    route: '/tenders',
    paths: withSkin('/tenders'),
  },
  'tender-overview': {
    route: '/tenders',
    paths: withSkin('/tenders'),
  },
  'tender-history': {
    route: '/tenders/history',
    paths: withSkin('/tenders/history'),
  },
  'tender-analytics': {
    route: '/tenders/analytics',
    paths: withSkin('/tenders/analytics'),
  },

  /****** View tender ******/
  'tender-view': {
    route: `/tenders/${tenderId}`,
    paths: withSkin(`/tenders/${tenderId}`),
  },
  'tender-trades': {
    route: `/tenders/${tenderId}/trades`,
    paths: withSkin(`/tenders/${tenderId}/trades`),
  },
  'tender-report': {
    route: `/tenders/${tenderId}/report`,
    paths: withSkin(`/tenders/${tenderId}/report`),
  },

  /****** Tender orders ******/
  'tender-orders': {
    route: `/tenders/${tenderId}/orders`,
    paths: withSkin(`/tenders/${tenderId}/orders`),
  },
  'tender-order-view': {
    route: `/tenders/${tenderId}/orders/${orderId}`,
    paths: withSkin(`/tenders/${tenderId}/orders/${orderId}`),
  },
  'tender-order-details': {
    route: `/tenders/${tenderId}/orders/${orderId}/details`,
    paths: withSkin(`/tenders/${tenderId}/orders/${orderId}/details`),
  },
  'tender-order-qa': {
    route: `/tenders/${tenderId}/orders/${orderId}/q-and-a`,
    paths: withSkin(`/tenders/${tenderId}/orders/${orderId}/q-and-a`),
  },
  'tender-order-history': {
    route: `/tenders/${tenderId}/orders/${orderId}/history`,
    paths: withSkin(`/tenders/${tenderId}/orders/${orderId}/history`),
  },
  'tender-order-buyers': {
    route: `/tenders/${tenderId}/orders/${orderId}/prices`,
    paths: withSkin(`/tenders/${tenderId}/orders/${orderId}/prices`),
  },

  /****** Tender counter orders ******/
  'tender-counter': {
    route: `/tenders/${tenderId}/orders/${orderId}/my-counter`,
    paths: withSkin(`/tenders/${tenderId}/orders/${orderId}/my-counter`),
  },
  'tender-counter-orders': {
    route: `/tenders/${tenderId}/orders/${orderId}/counters`,
    paths: withSkin(`/tenders/${tenderId}/orders/${orderId}/counters`),
  },
  'tender-counter-history': {
    route: `/tenders/${tenderId}/orders/${orderId}/counter-history`,
    paths: withSkin(`/tenders/${tenderId}/orders/${orderId}/counter-history`),
  },

  /****** Add/edit tenders ******/
  'tender-new': {
    route: '/tenders/new',
    paths: [
      `/${SKIN_ROUTE_PATTERN}/tenders/:mode(new|edit)/${tenderId}?`,
      `/tenders/:mode(new|edit)/${tenderId}?`,
    ],
  },
  'tender-edit-details': {
    route: `/tenders/:mode(new|edit)/${tenderId}?`,
    paths: withSkin(`/tenders/:mode(new|edit)/${tenderId}?`),
  },
  'tender-edit-orders': {
    route: `/tenders/:mode(new|edit)/${tenderId}/orders/${orderId}?`,
    paths: withSkin(`/tenders/:mode(new|edit)/${tenderId}/orders/${orderId}?`),
  },
  'tender-edit-participants': {
    route: `/tenders/:mode(new|edit)/${tenderId}/participants`,
    paths: withSkin(`/tenders/:mode(new|edit)/${tenderId}/participants`),
  },
  'tender-edit-fixedprice': {
    route: `/tenders/:mode(new|edit)/${tenderId}/fixedprice`,
    paths: withSkin(`/tenders/:mode(new|edit)/${tenderId}/fixedprice`),
  },
  'tender-edit-review': {
    route: `/tenders/:mode(new|edit)/${tenderId}/review`,
    paths: withSkin(`/tenders/:mode(new|edit)/${tenderId}/review`),
  },

  'tender-duplicate-order': {
    route: `/duplicate/tender/${tenderId}/order/${orderId}`,
    paths: withSkin(`/duplicate/tender/${tenderId}/order/${orderId}`),
  },
};

export function matchUrl<N extends IRouteName>(
  name: N,
  params?: IRouteProps<N>
) {
  const defn = routes[name];
  const paths = defn.paths || [defn.route];

  if (params) return paths.map((p) => generatePath(p, { ...params, subPath }));
  return paths;
}

export function matchMany(...names: IRouteName[]) {
  return names.flatMap((n) => matchUrl(n));
}

export function pathActive(path: IRouteName, url: string, exact = false) {
  return !!matchPath(url, { path: matchUrl(path), exact });
}

export function matchParams<N extends IRouteName>(
  path: N,
  url: string,
  exact = false
): Partial<IRouteProps<N>> {
  return matchPath(url, { path: matchUrl(path), exact })?.params || {};
}

export function usePathTools() {
  const { pathname } = useLocation();

  return {
    pathActive(path: IRouteName, exact = false) {
      return pathActive(path, pathname, exact);
    },

    matchParams<N extends IRouteName>(path: N, exact = false) {
      return matchParams(path, pathname, exact);
    },
  };
}
